import {chooseLayout, DvLayout, getLeafLayouts, getSelectedLayout, getViewItemUrl, LayoutContentType, TaskWidgetData, useAppDispatch, useAppSelector} from '@dv5/domain'
import {clipboardService, LayoutChooserList, LinkIcon} from '@dv5/components'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import {useTaskWidgetUpdate} from './useTaskWidgetUpdate'
import './TaskWidgetToolbar.less'

interface Props {
    canvasId: string
    widgetId: string
    data: TaskWidgetData
    readOnly?: boolean
    targetAvailable: boolean
    toolbarStyles: CSSProperties
    onNavigateToTask: (taskId: string, layoutId: string) => void
}

export const TaskWidgetToolbar: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    readOnly = false,
    targetAvailable,
    toolbarStyles,
    onNavigateToTask,
}) => {
    const dispatch = useAppDispatch()
    const {updateData} = useTaskWidgetUpdate({canvasId, widgetId})
    const leafLayouts = useAppSelector(getLeafLayouts)
    const selectedLayout = useAppSelector(getSelectedLayout)
    const [openDropdown, setOpenDropdown] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpenDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleChooseLayout = (layout: DvLayout) => {
        dispatch(chooseLayout(layout))
        setOpenDropdown(false)
    }

    const handleNavigate = () => {
        if (!targetAvailable || !data.taskId || !selectedLayout) {
            return
        }
        onNavigateToTask(data.taskId, selectedLayout.id)
    }

    const handleCopyLink = () => {
        if (!data.taskId || !selectedLayout) {
            return
        }
        clipboardService.copyTo(
            getViewItemUrl(window.location.origin, selectedLayout.id, data.taskId, LayoutContentType.TASK),
            false
        )
    }
    const toggleOpenDropDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        setOpenDropdown(!openDropdown)
    }
    const toggleEstimation = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        updateData(['hideEstimation'], {...data, hideEstimation: !data.hideEstimation})
    }
    const toggleDescription = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        updateData(['hideDescription'], {...data, hideDescription: !data.hideDescription})
    }

    return (
        <div className='task-widget-toolbar' style={toolbarStyles} ref={dropdownRef}
             onMouseDown={(e) => e.stopPropagation()}>
            {leafLayouts.length > 0 && (
                <div className='task-widget-toolbar__layout-selector'>
                    <button
                        className={`task-widget-toolbar__button ${openDropdown ? 'task-widget-toolbar__button--active' : ''}`}
                        onMouseDown={toggleOpenDropDown}
                        aria-label='Choose layout for navigation'
                        type='button'
                        title={selectedLayout ? selectedLayout.title : 'Select layout'}
                    >
                        <span className='task-widget-toolbar__layout-title'>
                            {selectedLayout ? selectedLayout.title : 'Layout'}
                        </span>
                        <span className='task-widget-toolbar__dropdown-caret'>▾</span>
                    </button>
                    {openDropdown && (
                        <div className='task-widget-toolbar__dropdown'>
                            <LayoutChooserList
                                layouts={leafLayouts}
                                selectedLayoutId={selectedLayout?.id}
                                onSelect={handleChooseLayout}
                            />
                        </div>
                    )}
                </div>
            )}
            {leafLayouts.length > 0 && <div className='task-widget-toolbar__divider'/>}
            <button
                className={`task-widget-toolbar__button ${!data.hideEstimation ? 'task-widget-toolbar__button--active' : ''}`}
                disabled={readOnly}
                onMouseDown={toggleEstimation}
                aria-label={data.hideEstimation ? 'Show estimation' : 'Hide estimation'}
                type='button'
                title='Show/hide estimation'
            >
                Estimation
            </button>
            <button
                className={`task-widget-toolbar__button ${!data.hideDescription ? 'task-widget-toolbar__button--active' : ''}`}
                disabled={readOnly}
                onMouseDown={toggleDescription}
                aria-label={data.hideDescription ? 'Show description' : 'Hide description'}
                type='button'
                title='Show/hide description'
            >
                Description
            </button>
            <div className='task-widget-toolbar__divider'/>
            <button
                className='task-widget-toolbar__button'
                onClick={handleCopyLink}
                disabled={!data.taskId || !selectedLayout}
                aria-label='Copy link'
                type='button'
                title='Copy link'
            >
                <LinkIcon />
            </button>
            <div className='task-widget-toolbar__divider'/>
            <button
                className='task-widget-toolbar__button task-widget-toolbar__button--navigate'
                onClick={handleNavigate}
                disabled={!targetAvailable || !data.taskId || !selectedLayout}
                aria-label='Open task in selected layout'
                type='button'
                title='Open task in selected layout'
            >
                →
            </button>
        </div>
    )
}
